/**
 * Upnova (#upezCart) cart-drawer guard.
 *
 * 1) Layout: keep line items visible — items stay natural height so the
 *    native column scrolls; compact sticky header (free-shipping progress
 *    rail + marquee + title padding) so short viewports are not stuck at
 *    one-item-at-a-time; Checkout+ footer already compacted separately.
 * 2) Onward: hide Checkout+ / uninsure when the cart has no shippable
 *    merchandise (CL+ membership / digital-only). Delivery protection does not
 *    apply, and the dual Checkout+ buttons steal the items viewport.
 * 3) ConvertCart overlays (.cc-exit-* exit-intent AND .cc-mainpopup* /
 *    #czrmn-widget gamification popups like "pick a harvest box") must not
 *    sit on top of the cart drawer or /cart — they block Remove / qty /
 *    Checkout+ clicks. Suppress while the cart surface is open; restore after.
 *
 * Pierces the open #upezCart shadow root only; no changes to theme.min.js.
 * Keep CSS/attribute-only inside Upnova's shadow tree — never reparent nodes.
 *
 * IMPORTANT: Do NOT re-fetch /cart.js from the shadow MutationObserver.
 * Upnova animates progress bars / marquees continuously; observing those
 * mutations and calling cart.js every debounce window storms Shopify (HTTP
 * 429) and makes drawer qty/Remove appear broken. Layout re-apply is fine;
 * cart.js only on real cart mutations, UPEZ cart events, open, or boot.
 */
(function () {
  'use strict';

  if (window.__CLNF_CART_DRAWER_GUARD__) return;
  window.__CLNF_CART_DRAWER_GUARD__ = true;

  var STYLE_ATTR = 'data-clnf-cart-drawer-guard';
  var HOST_DIGITAL_ATTR = 'data-clnf-digital-only';
  var HOST_HIDE_ONWARD_ATTR = 'data-clnf-hide-onward';
  var CC_SUPPRESS_ATTR = 'data-clnf-suppressed-over-cart';
  var ONWARD_HANDLE_RE = /onward/i;
  var ATTR_SYNC_MS = 1500;
  var LAYOUT_DEBOUNCE_MS = 120;
  var CART_FETCH_DEBOUNCE_MS = 200;
  var CART_FETCH_MIN_MS = 2000;
  var lastAttrSyncAt = 0;
  var lastCartFetchAt = 0;
  var lastDigitalOnly = null;
  var cartFetchInFlight = null;
  var hostObserver = null;
  var ccExitObserver = null;
  var ccExitRaf = null;
  var layoutTimer = null;
  var cartFetchTimer = null;
  var watchedHost = null;
  var applying = false;

  var GUARD_CSS = [
    '/*',
    '  Checkout+ lives in Upnova\'s sticky footer (~300px). If the items',
    '  region is flex-grow + flex-shrink, every line item shrinks into the',
    '  leftover band and they stack on each other. Keep rows at natural',
    '  height and let the native column (.overflow-y-auto) scroll.',
    '*/',
    '.flex.flex-col.gap-2.px-4.py-2.grow {',
    '  flex: 0 0 auto !important;',
    '  overflow: visible !important;',
    '}',
    '.flex.flex-col.gap-2.px-4.py-2.grow > * {',
    '  flex: 0 0 auto !important;',
    '  min-height: auto !important;',
    '}',
    '.flex.flex-col.gap-2.py-2.mt-0 {',
    '  flex: 0 0 auto !important;',
    '  max-height: none !important;',
    '  overflow: visible !important;',
    '}',
    '/* Digital-only (no shippable merchandise): drop shipping goals + Onward UI */',
    ':host([' + HOST_DIGITAL_ATTR + ']) .upez__shop_progress_pristine {',
    '  display: none !important;',
    '}',
    ':host([' + HOST_HIDE_ONWARD_ATTR + ']) #onward-container,',
    ':host([' + HOST_HIDE_ONWARD_ATTR + ']) .onward-opt-out-btn,',
    ':host([' + HOST_HIDE_ONWARD_ATTR + ']) .onward-opt-out-link,',
    ':host([' + HOST_HIDE_ONWARD_ATTR + ']) .checkout-plus,',
    ':host([' + HOST_HIDE_ONWARD_ATTR + ']) section.onward-font-inter {',
    '  display: none !important;',
    '  height: 0 !important;',
    '  max-height: 0 !important;',
    '  overflow: hidden !important;',
    '  padding: 0 !important;',
    '  margin: 0 !important;',
    '  border: 0 !important;',
    '}',
    ':host([' + HOST_HIDE_ONWARD_ATTR + ']) .p-4.pt-4.flex.flex-col {',
    '  padding-top: 0.5rem !important;',
    '}',
    '',
    '/*',
    '  Compact Checkout+ — every viewport.',
    '',
    '  Onward grows the drawer\'s sticky footer by ~156px (widget 68 +',
    '  #onward-container margins 24 + Checkout+ button 56 + opt-out 64). The',
    '  widget and the two buttons are the product; the margins and the wrapper',
    '  padding are not. These rules reclaim ~58px of that.',
    '',
    '  Deliberately NOT behind a width media query. The drawer\'s sticky chrome',
    '  is a constant height on every device, so short viewports are squeezed',
    '  regardless of width — measured with 3 items, the scrollable product band',
    '  is 150px on a 1280x680 laptop, worse than a 375x667 phone. A 768px gate',
    '  would also make the cart abruptly chunkier one pixel above the',
    '  breakpoint. GUARD_CSS is injected only into the #upezCart shadow root,',
    '  so widening this cannot reach the /cart page — Onward\'s "breathing room',
    '  around widget on cart page" survives untouched at all widths.',
    '',
    '  Specificity: Onward injects its own !important rules into this same',
    '  shadow root (its sheet carries "Repaint drawer opt-in button..."), and',
    '  whichever <style> lands last would otherwise win. Every selector below',
    '  is prefixed with :host so it outranks Onward on specificity instead of',
    '  source order — no re-appending, no observer ping-pong.',
    '*/',
    '/* Widget breathing room 12/12 -> 4/4 (saves 16px). Onward sets these for',
    '   the cart page, where there is room; the drawer has none. */',
    ':host #onward-container {',
    '  margin-top: 4px !important;',
    '  margin-bottom: 4px !important;',
    '}',
    '/* Benefit line wraps to two lines under ~390px — tighten it (~3px) */',
    ':host #onward-container .onward-copy-line-2 {',
    '  line-height: 1.25 !important;',
    '  margin-top: 2px !important;',
    '}',
    '/* Opt-out: black fill, white text, 44px (saves 14px).',
    '   44px is the minimum tap target — do not go lower even on desktop, the',
    '   same drawer serves touch laptops. Grey fill read as disabled; #fff on',
    '   #000 is ~21:1 (WCAG AAA). Hover/focus stay dark so Onward cannot revert',
    '   the control to a muted grey. */',
    ':host .onward-opt-out-link.onward-opt-out-btn {',
    '  height: 2.75rem !important;',
    '  line-height: 2.75rem !important;',
    '  margin-top: 0.375rem !important;',
    '  background: #000 !important;',
    '  border: 1px solid #000 !important;',
    '  color: #fff !important;',
    '  font-size: 0.85rem !important;',
    '  font-weight: 400 !important;',
    '  cursor: pointer !important;',
    '}',
    ':host .onward-opt-out-link.onward-opt-out-btn:hover,',
    ':host .onward-opt-out-link.onward-opt-out-btn:focus,',
    ':host .onward-opt-out-link.onward-opt-out-btn:focus-visible {',
    '  background: #1a1a1a !important;',
    '  border-color: #1a1a1a !important;',
    '  color: #fff !important;',
    '}',
    '/* Upnova chrome wrapping the Onward block: the empty 13px strip under',
    '   Total, and the 16px/16px wrapper padding (saves ~30px). These are',
    '   Tailwind class selectors and so are the first rules here to break if',
    '   Upnova ships a new build — they degrade to current spacing, nothing',
    '   more. Scoped to the sticky footer so they cannot match elsewhere. */',
    ':host .sticky.bottom-0 > .px-4.pb-3 {',
    '  padding-bottom: 0 !important;',
    '}',
    ':host .sticky.bottom-0 > .p-4.pt-4.flex.flex-col {',
    '  padding-top: 0.5rem !important;',
    '  padding-bottom: 0.5rem !important;',
    '}',
    '',
    '/*',
    '  Compact sticky header — every viewport.',
    '',
    '  Trim marquee + title padding only. The free-shipping / BULK progress',
    '  rail (sentence, fill bar, milestone icons + labels) renders at',
    '  Upnova\'s native size: customers need to see the shipping tiers, so',
    '  do NOT clamp .upez__shop_progress_pristine or hide its .box-border.grid',
    '  children (the fill bar and the goal row both carry those classes).',
    '*/',
    ':host .upez__marquee {',
    '  padding-top: 0.125rem !important;',
    '  padding-bottom: 0.125rem !important;',
    '  line-height: 1.15 !important;',
    '  font-size: 12px !important;',
    '}',
    ':host .sticky.top-0 > .flex.items-center.justify-between.p-4 {',
    '  padding-top: 0.5rem !important;',
    '  padding-bottom: 0.5rem !important;',
    '}'
  ].join('\n');

  function isOnwardLine(item) {
    if (!item) return false;
    if (ONWARD_HANDLE_RE.test(item.handle || '')) return true;
    if (ONWARD_HANDLE_RE.test(item.product_type || '')) return true;
    if (ONWARD_HANDLE_RE.test(item.title || '')) return true;
    var url = item.url || '';
    return url.indexOf('/products/onward') !== -1;
  }

  function cartHasShippableMerchandise(cart) {
    var items = cart && cart.items;
    if (!items || !items.length) return false;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (isOnwardLine(item)) continue;
      if (item.requires_shipping) return true;
    }
    return false;
  }

  function isDigitalOnlyCart(cart) {
    if (!cart || !cart.items || !cart.items.length) return false;
    return !cartHasShippableMerchandise(cart);
  }

  function getHost() {
    return document.getElementById('upezCart');
  }

  function isUpnovaCartOpen(host) {
    host = host || getHost();
    if (!host) return false;
    var cs = window.getComputedStyle(host);
    if (cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) === 0) {
      return false;
    }
    var sr = host.shadowRoot;
    var panel = sr && (sr.querySelector('.fixed.right-0') || sr.querySelector('.fixed.inset-0'));
    if (panel) {
      var pcs = window.getComputedStyle(panel);
      if (pcs.display === 'none' || pcs.visibility === 'hidden' || Number(pcs.opacity) === 0) {
        return false;
      }
      var prect = panel.getBoundingClientRect();
      if (prect.width >= 8 && prect.height >= 8) return true;
    }
    var rect = host.getBoundingClientRect();
    if (rect.width < 8 || rect.height < 8) return false;
    if (rect.width < window.innerWidth * 0.2 && rect.height < window.innerHeight * 0.2) {
      return false;
    }
    if (host.getAttribute('aria-hidden') === 'true' || host.hasAttribute('hidden')) {
      return false;
    }
    return true;
  }

  function weThemeRightDrawerVisiblyOpen() {
    var drawer = document.querySelector('.drawer--right.drawer--cart');
    if (!drawer) return false;
    var cs = window.getComputedStyle(drawer);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    var rect = drawer.getBoundingClientRect();
    return rect.width > 8 && rect.height > 8;
  }

  function isCartDrawerOpen() {
    var body = document.body;
    if (body && (body.classList.contains('js-drawer-open-right') || weThemeRightDrawerVisiblyOpen())) {
      return true;
    }
    return isUpnovaCartOpen();
  }

  /**
   * Drives the mobile bottom nav's cart-open hiding (clnf-bottom-nav.css
   * `.cc-hide-sticky90034942`). Deliberately NOT keyed off `body[style*="overflow:
   * hidden"]`: that inline style is also set by Prop 65 / water-intake modals and,
   * per passive-events-patch.js, can get stuck after Upnova's close/empty-cart path
   * skips cleanup ("the page stays tappable but frozen") — a leftover style would
   * hide the nav permanently. isCartDrawerOpen() reads actual visibility instead, so
   * a stuck style can no longer strand the nav hidden.
   *
   * Kept from main (PR #267+) when re-landing the /cart.js 429-storm fix so this
   * branch does not regress bottom-nav visibility.
   */
  function syncBottomNavCartVisibility() {
    if (!document.body) return;
    document.body.classList.toggle('cc-hide-sticky90034942', isCartDrawerOpen());
  }
  window.__CLNF_isCartDrawerOpen = isCartDrawerOpen;

  function isCartPage() {
    try {
      return /^\/cart\/?$/i.test(window.location.pathname || '');
    } catch (e) {
      return false;
    }
  }

  /**
   * Cart drawer OR full /cart page — surfaces where line-item Remove / qty /
   * Checkout+ must remain clickable. ConvertCart mainpopup + exit overlays on
   * top of these steal pointer events and look like a broken cart.
   */
  function shouldSuppressConvertCartOverlays() {
    return isCartDrawerOpen() || isCartPage();
  }

  /**
   * ConvertCart campaign roots that paint full-viewport / centered modals.
   * Keep this narrow — do NOT match .cch-* search, .cc-shop-* bottom nav, etc.
   */
  function queryConvertCartOverlayRoots() {
    return document.querySelectorAll(
      '[class*="cc-exit-"],[class*="cc-mainpopup"],#czrmn-widget,.cc-exit-backdrop'
    );
  }

  function hasConvertCartOverlayPresence() {
    return (
      !!document.querySelector('[class*="cc-exit-"]') ||
      !!document.querySelector('[class*="cc-mainpopup"]') ||
      !!document.getElementById('czrmn-widget') ||
      !!document.querySelector('.cc-exit-backdrop') ||
      !!document.querySelector('[' + CC_SUPPRESS_ATTR + ']')
    );
  }

  function ensureShadowStyles(host) {
    var sr = host && host.shadowRoot;
    if (!sr) return false;
    var style = sr.querySelector('style[' + STYLE_ATTR + ']');
    if (!style) {
      style = document.createElement('style');
      style.setAttribute(STYLE_ATTR, '1');
      sr.appendChild(style);
    }
    if (style.textContent !== GUARD_CSS) style.textContent = GUARD_CSS;
    return true;
  }

  function setHostFlags(host, digitalOnly) {
    if (!host) return;
    if (digitalOnly) {
      host.setAttribute(HOST_DIGITAL_ATTR, '');
      host.setAttribute(HOST_HIDE_ONWARD_ATTR, '');
    } else {
      host.removeAttribute(HOST_DIGITAL_ATTR);
      host.removeAttribute(HOST_HIDE_ONWARD_ATTR);
    }
  }

  function onwardShadowNodes(sr) {
    return sr.querySelectorAll(
      '#onward-container, .onward-opt-out-btn, .onward-opt-out-link, .checkout-plus, section.onward-font-inter'
    );
  }

  function hideOnwardInShadow(host) {
    var sr = host && host.shadowRoot;
    if (!sr) return;
    var nodes = onwardShadowNodes(sr);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-clnf-onward-suppressed') === '1') continue;
      nodes[i].setAttribute('data-clnf-onward-suppressed', '1');
      nodes[i].style.setProperty('display', 'none', 'important');
    }
  }

  function restoreOnwardInShadow(host) {
    var sr = host && host.shadowRoot;
    if (!sr) return;
    var nodes = sr.querySelectorAll('[data-clnf-onward-suppressed]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].removeAttribute('data-clnf-onward-suppressed');
      nodes[i].style.removeProperty('display');
    }
  }

  function uninsureIfPossible() {
    try {
      if (
        window.onwardClient &&
        typeof window.onwardClient.uninsureOrderIfRequired === 'function'
      ) {
        window.onwardClient.uninsureOrderIfRequired();
      }
    } catch (e) {}
  }

  function syncCartAttributes(digitalOnly) {
    var now = Date.now();
    if (now - lastAttrSyncAt < ATTR_SYNC_MS) return;
    lastAttrSyncAt = now;

    fetch('/cart.js?clnf_guard=1', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (!cart || !cart.item_count) return;
        var current = cart.attributes || {};
        var desired = {};

        if (digitalOnly) {
          desired._hideOnwardCheckoutExtension = 'true';
          desired._onwardInsured = 'false';
        } else {
          // Clear only our suppress values; leave a real opt-in ('true') alone.
          if ((current._hideOnwardCheckoutExtension || '') === 'true') {
            desired._hideOnwardCheckoutExtension = '';
          }
          if ((current._onwardInsured || '') === 'false') {
            desired._onwardInsured = '';
          }
          if (!Object.keys(desired).length) return;
        }

        var differs = false;
        var key;
        for (key in desired) {
          if (!Object.prototype.hasOwnProperty.call(desired, key)) continue;
          if ((current[key] || '') !== desired[key]) {
            differs = true;
            break;
          }
        }
        if (!differs) return;
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: desired })
        });
      })
      .catch(function () {});
  }

  function applyForCart(cart) {
    applying = true;
    try {
      syncBottomNavCartVisibility();

      var host = getHost();
      if (host) ensureShadowStyles(host);

      var digitalOnly = isDigitalOnlyCart(cart);
      if (host) {
        setHostFlags(host, digitalOnly);
        if (digitalOnly) {
          hideOnwardInShadow(host);
        } else {
          restoreOnwardInShadow(host);
        }
      }

      if (digitalOnly !== lastDigitalOnly) {
        var wasDigitalOnly = lastDigitalOnly;
        lastDigitalOnly = digitalOnly;
        if (digitalOnly) {
          uninsureIfPossible();
          syncCartAttributes(true);
        } else if (wasDigitalOnly === true) {
          // Only clear suppress attrs when leaving digital-only — never wipe a
          // customer's intentional Onward opt-in on first shippable-cart observe.
          syncCartAttributes(false);
        }
      } else if (digitalOnly) {
        if (host) hideOnwardInShadow(host);
        uninsureIfPossible();
      }
    } finally {
      setTimeout(function () {
        applying = false;
      }, 0);
    }
  }

  /**
   * Re-apply CSS + last-known digital-only flags without hitting /cart.js.
   * Safe to call from the shadow MutationObserver (Upnova animates often).
   */
  function applyLayoutOnly() {
    applying = true;
    try {
      syncBottomNavCartVisibility();

      var host = getHost();
      if (!host) return;
      ensureShadowStyles(host);
      if (lastDigitalOnly === true) {
        setHostFlags(host, true);
        hideOnwardInShadow(host);
      } else if (lastDigitalOnly === false) {
        setHostFlags(host, false);
        restoreOnwardInShadow(host);
      } else {
        // Unknown cart shape yet — still inject layout CSS.
        setHostFlags(host, false);
      }
    } finally {
      setTimeout(function () {
        applying = false;
      }, 0);
    }
  }

  function scheduleLayoutOnly() {
    if (layoutTimer) clearTimeout(layoutTimer);
    layoutTimer = setTimeout(function () {
      layoutTimer = null;
      applyLayoutOnly();
      dismissConvertCartExitIfCartOpen();
    }, LAYOUT_DEBOUNCE_MS);
  }

  function fetchCartAndApply(force) {
    var now = Date.now();
    if (!force && now - lastCartFetchAt < CART_FETCH_MIN_MS) {
      // Still refresh layout/overlay suppress; skip another cart.js.
      scheduleLayoutOnly();
      return cartFetchInFlight || Promise.resolve(null);
    }
    if (cartFetchInFlight) return cartFetchInFlight;
    lastCartFetchAt = now;
    cartFetchInFlight = fetch('/cart.js?clnf_guard=1', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        applyForCart(cart);
        return cart;
      })
      .catch(function () {
        return null;
      })
      .then(function (cart) {
        cartFetchInFlight = null;
        return cart;
      });
    return cartFetchInFlight;
  }

  /**
   * Schedule a /cart.js read. Prefer UPEZ cart events when available; this is
   * the fallback for cart open / mutation / boot. Never call from MutationObserver.
   */
  function scheduleCartFetch(force) {
    if (cartFetchTimer) clearTimeout(cartFetchTimer);
    cartFetchTimer = setTimeout(function () {
      cartFetchTimer = null;
      var run = function () {
        fetchCartAndApply(!!force);
      };
      if (window.loadScheduler && typeof window.loadScheduler.now === 'function') {
        window.loadScheduler.now(run);
      } else {
        run();
      }
      dismissConvertCartExitIfCartOpen();
    }, CART_FETCH_DEBOUNCE_MS);
  }

  function isCartMutationUrl(url) {
    if (!url) return false;
    var path = String(url).split('?')[0];
    return /\/cart\/(add|change|update|clear)(\.js)?$/i.test(path);
  }

  function isCartContentsMutationUrl(url) {
    if (!url) return false;
    var path = String(url).split('?')[0];
    // Attribute-only /cart/update.js spam from Upnova/apps must not force
    // unbounded cart.js reads — only line-item mutations do.
    return /\/cart\/(add|change|clear)(\.js)?$/i.test(path);
  }

  function hideCcExitNode(el) {
    if (!el || el.getAttribute(CC_SUPPRESS_ATTR) === '1') return;
    el.setAttribute(CC_SUPPRESS_ATTR, '1');
    el.style.setProperty('display', 'none', 'important');
    el.style.setProperty('pointer-events', 'none', 'important');
    el.style.setProperty('visibility', 'hidden', 'important');
  }

  function restoreSuppressedCcExitNodes() {
    var nodes = document.querySelectorAll('[' + CC_SUPPRESS_ATTR + ']');
    var i;
    for (i = 0; i < nodes.length; i++) {
      nodes[i].removeAttribute(CC_SUPPRESS_ATTR);
      nodes[i].style.removeProperty('display');
      nodes[i].style.removeProperty('pointer-events');
      nodes[i].style.removeProperty('visibility');
    }
  }

  function dismissConvertCartExitIfCartOpen() {
    if (!shouldSuppressConvertCartOverlays()) {
      restoreSuppressedCcExitNodes();
      return;
    }

    // Prefer clicking a close control so ConvertCart can clean up its own state.
    var closeBtn = document.querySelector(
      '[class*="cc-mainpopup"] .cc-popup-close,' +
        '[class*="cc-mainpopup"] [class*="close"],' +
        '#czrmn-widget .cc-popup-close,' +
        '.cc-exit-backdrop ~ [class*="cc-exit-"] .cc-popup-close,' +
        '[class*="cc-exit-"] .cc-popup-close,' +
        '.cc-popup-close'
    );
    if (closeBtn) {
      try {
        closeBtn.click();
      } catch (e) {}
    }

    var roots = queryConvertCartOverlayRoots();
    var i;
    for (i = 0; i < roots.length; i++) {
      hideCcExitNode(roots[i]);
    }
  }

  function ccExitWatcherTick() {
    ccExitRaf = null;
    // Avoid scanning the whole document on every mutation site-wide (Vue
    // re-renders, carousels, lazy image swaps) when no ConvertCart overlay
    // is present — without this guard it forces work on every mutation.
    if (!hasConvertCartOverlayPresence()) {
      return;
    }
    if (shouldSuppressConvertCartOverlays()) {
      dismissConvertCartExitIfCartOpen();
    } else {
      restoreSuppressedCcExitNodes();
    }
  }

  function ccOverlaySelectorMatches(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.id === 'czrmn-widget') return true;
    var cls = (node.getAttribute && node.getAttribute('class')) || '';
    if (
      cls.indexOf('cc-exit-') !== -1 ||
      cls.indexOf('cc-mainpopup') !== -1 ||
      cls.indexOf('cc-exit-backdrop') !== -1
    ) {
      return true;
    }
    return !!(
      node.querySelector &&
      node.querySelector('[class*="cc-exit-"],[class*="cc-mainpopup"],#czrmn-widget,.cc-exit-backdrop')
    );
  }

  function ccMutationTargetIsRelevant(node) {
    if (!node || node.nodeType !== 1) return false;
    // document.body's own class (js-drawer-open-right) and the Upnova/WeTheme
    // cart-drawer surfaces are what isCartDrawerOpen() reads to decide
    // open/closed; keep those relevant even though they aren't CC markup.
    if (node === document.body || node.id === 'upezCart') return true;
    if (node.matches && node.matches('.drawer--right.drawer--cart')) return true;
    return ccOverlaySelectorMatches(node);
  }

  /**
   * ccExitObserver has to stay attached to document.body for the whole page
   * (ConvertCart's exit-intent popup can fire minutes into a session), but
   * without filtering it schedules a full ccExitWatcherTick() — 5x
   * document.querySelector() over the whole DOM — on every single unrelated
   * body mutation site-wide (Vue re-renders, carousels, lazy image swaps),
   * forever. Only a cart-drawer state change or a node that looks like
   * ConvertCart overlay markup should trigger a check.
   */
  function ccMutationsAreRelevant(mutations) {
    var i, m, j;
    for (i = 0; i < mutations.length; i++) {
      m = mutations[i];
      if (m.type === 'attributes') {
        if (ccMutationTargetIsRelevant(m.target)) return true;
        continue;
      }
      for (j = 0; j < m.addedNodes.length; j++) {
        if (ccMutationTargetIsRelevant(m.addedNodes[j])) return true;
      }
      for (j = 0; j < m.removedNodes.length; j++) {
        if (ccMutationTargetIsRelevant(m.removedNodes[j])) return true;
      }
    }
    return false;
  }

  function ensureCcExitWatcher() {
    if (ccExitObserver || typeof MutationObserver === 'undefined' || !document.body) {
      return;
    }
    ccExitObserver = new MutationObserver(function (mutations) {
      if (ccExitRaf || !ccMutationsAreRelevant(mutations)) return;
      ccExitRaf = requestAnimationFrame(ccExitWatcherTick);
    });
    ccExitObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function watchHost() {
    var host = getHost();
    if (!(host && host.shadowRoot)) return false;

    ensureShadowStyles(host);
    ensureCcExitWatcher();

    if (watchedHost !== host) {
      if (hostObserver) {
        try {
          hostObserver.disconnect();
        } catch (e) {}
        hostObserver = null;
      }
      watchedHost = host;
      // One cart.js on first attach only — later shadow mutations must NOT re-fetch.
      scheduleCartFetch(true);
      hostObserver = new MutationObserver(function () {
        if (applying) return;
        // Layout/CSS only. Upnova progress/marquee animations mutate the
        // shadow tree constantly — never re-fetch cart.js from here.
        scheduleLayoutOnly();
      });
      hostObserver.observe(host.shadowRoot, {
        childList: true,
        subtree: true
      });
    } else {
      scheduleLayoutOnly();
    }
    return true;
  }

  function boot() {
    if (watchHost()) {
      dismissConvertCartExitIfCartOpen();
      return;
    }

    var tries = 0;
    var maxTries = 40;
    var timer = setInterval(function () {
      tries += 1;
      if (watchHost() || tries >= maxTries) clearInterval(timer);
    }, 250);

    var bodyObserver = new MutationObserver(function () {
      if (watchHost()) bodyObserver.disconnect();
    });
    if (document.body) {
      bodyObserver.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () {
        bodyObserver.disconnect();
      }, 15000);
      ensureCcExitWatcher();
      dismissConvertCartExitIfCartOpen();
    }
  }

  function patchFetch() {
    if (typeof window.fetch !== 'function' || window.fetch.__clnfCartDrawerGuard) {
      return;
    }
    var original = window.fetch;
    function wrapped(input, init) {
      var url = '';
      try {
        url =
          typeof input === 'string'
            ? input
            : input && input.url
              ? String(input.url)
              : '';
      } catch (e) {
        url = '';
      }
      var result = original.apply(this, arguments);
      if (isCartMutationUrl(url) && result && typeof result.then === 'function') {
        result.then(
          function () {
            scheduleCartFetch(isCartContentsMutationUrl(url));
          },
          function () {}
        );
      }
      return result;
    }
    wrapped.__clnfCartDrawerGuard = true;
    window.fetch = wrapped;
  }

  function patchXHR() {
    if (!window.XMLHttpRequest || XMLHttpRequest.prototype.__clnfCartDrawerGuard) {
      return;
    }
    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__clnfCartUrl = url;
      return open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (isCartMutationUrl(this.__clnfCartUrl)) {
        var mutationUrl = this.__clnfCartUrl;
        this.addEventListener('loadend', function () {
          scheduleCartFetch(isCartContentsMutationUrl(mutationUrl));
        });
      }
      return send.apply(this, arguments);
    };
    XMLHttpRequest.prototype.__clnfCartDrawerGuard = true;
  }

  patchFetch();
  patchXHR();

  // Prefer Upnova's already-fetched cart payload over another /cart.js round-trip.
  function onUpezCartEvent(e) {
    var cart = e && e.detail && e.detail.cart;
    if (cart && typeof cart === 'object') {
      applyForCart(cart);
      dismissConvertCartExitIfCartOpen();
      return;
    }
    scheduleCartFetch(true);
  }
  document.addEventListener('UPEZ__CART_REFETCHED_AFTER_CHANGE', onUpezCartEvent);
  document.addEventListener('UPEZ__CART_REFETCHED_AFTER_ADD', onUpezCartEvent);

  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (
        t.closest('[data-upez-cart="upez"]') ||
        t.closest('#upezCart') ||
        t.closest('.js-drawer-open-right-link')
      ) {
        setTimeout(function () {
          scheduleLayoutOnly();
        }, 50);
        setTimeout(function () {
          scheduleCartFetch(false);
        }, 400);
        setTimeout(dismissConvertCartExitIfCartOpen, 100);
      }
    },
    true
  );

  window.addEventListener('onward:initialized', function () {
    scheduleCartFetch(false);
  });

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) scheduleCartFetch(false);
  });

  // WeTheme native drawer toggles `js-drawer-open-right` on <body>. Closing via
  // the backdrop (not a selector the click delegate above watches) would not
  // otherwise re-run syncBottomNavCartVisibility(), so watch the class directly.
  if (typeof MutationObserver === 'function' && document.body) {
    new MutationObserver(syncBottomNavCartVisibility).observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      syncBottomNavCartVisibility();
      boot();
    });
  } else {
    syncBottomNavCartVisibility();
    boot();
  }
})();
